import React, { useEffect, useRef, useState, memo } from "react";
import { ChevronDown, CircleHelp, Heart, MapPin, Music2, Users } from "lucide-react";
import { Chip, ReadableCard } from "../Sticker";
import ProfileThumbnail from "../ProfileThumbnail";
import LinkifiedText from "../LinkifiedText";
import { ROLE_COLORS } from "../../lib/dance";
import { attendanceLabel, attendanceStatus } from "../../lib/rsvp";

const FOLLOW_COPY = {
  series: {
    tooltip:
      "Follow to get schedule updates and day-of RSVP reminders—no need to lock in yet.",
    emptyHint:
      "Not sure you can make it? Follow to hear when others sign up.",
  },
  proposed: {
    tooltip:
      "This night isn't confirmed yet. Following shows interest—if enough dancers follow, hosts can lock in a real date.",
    emptyHint:
      "Not ready to RSVP? Follow so you'll know if this night gets confirmed.",
  },
};

function followCountLabel(count) {
  const n = Number(count) || 0;
  return `${n} ${n === 1 ? "dancer" : "dancers"} following`;
}

const EventFollowRow = memo(function EventFollowRow({
  followerCount = 0,
  isFollowing = false,
  followBusy = false,
  onToggleFollow,
  followCopy = FOLLOW_COPY.series,
}) {
  const [tipOpen, setTipOpen] = useState(false);

  return (
    <div className="mb-4 pt-3 border-t border-subtle" data-testid="event-follow-row">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex items-center gap-2">
          <Users className="w-4 h-4 shrink-0 text-ink-secondary" strokeWidth={2.5} aria-hidden />
          <p className="min-w-0 font-ui text-[14px] font-medium text-ink leading-snug flex items-center gap-1.5 flex-nowrap">
            <span data-testid="event-follow-count" className="min-w-0 truncate">{followCountLabel(followerCount)}</span>
            <button
              type="button"
              className="inline-flex items-center justify-center w-7 h-7 shrink-0 -m-1 rounded-btn text-ink-secondary hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink/30"
              aria-expanded={tipOpen}
              aria-controls="event-follow-tooltip"
              aria-label="What does following mean?"
              data-testid="event-follow-help-btn"
              onClick={() => setTipOpen((open) => !open)}
            >
              <CircleHelp className="w-4 h-4" strokeWidth={2.5} aria-hidden />
            </button>
          </p>
        </div>
        {isFollowing ? (
          <div
            className="shrink-0 inline-flex items-center justify-center min-h-[40px] px-3.5 py-2 rounded-btn font-ui text-[15px] font-semibold bg-[color:color-mix(in_srgb,var(--live)_20%,transparent)] border border-[color:color-mix(in_srgb,var(--live)_35%,transparent)] text-ink"
            data-testid="event-following-status"
            aria-label="You are following this event"
          >
            Following
          </div>
        ) : (
          <button
            type="button"
            onClick={onToggleFollow}
            disabled={followBusy || !onToggleFollow}
            className="host-secondary-btn shrink-0 inline-flex items-center justify-center min-h-[40px] px-3.5 py-2 rounded-btn font-ui text-[15px] font-semibold transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink/30 disabled:opacity-60 bg-surface-muted border border-subtle text-ink hover:border-[color:var(--host-cta)] hover:text-white"
            data-testid="event-follow-btn"
          >
            {followBusy ? "Following…" : "Follow"}
          </button>
        )}
      </div>
      {tipOpen && (
        <p
          id="event-follow-tooltip"
          role="note"
          className="mt-2 font-ui text-[14px] font-medium text-ink-secondary leading-relaxed"
          data-testid="event-follow-tooltip"
        >
          {followCopy.tooltip}
        </p>
      )}
    </div>
  );
});

const BalanceChart = memo(function BalanceChart({ counts }) {
  const L = counts.balanced_leaders;
  const F = counts.balanced_followers;
  const splitTotal = L + F;
  if (splitTotal === 0) return null;

  const lpct = (L / splitTotal) * 100;

  return (
    <div className="w-full" data-testid="balance-chart">
      <div
        className="rounded-btn h-2.5 bg-surface-muted border border-subtle overflow-hidden flex"
        data-testid="balance-bar"
        role="img"
        aria-label={`${Math.round(L)} leads, ${Math.round(F)} follows`}
      >
        <div
          className="bg-leaders h-full transition-all duration-500 motion-reduce:transition-none"
          style={{ width: `${lpct}%` }}
        />
        <div
          className="bg-followers h-full transition-all duration-500 motion-reduce:transition-none"
          style={{ width: `${100 - lpct}%` }}
        />
      </div>
    </div>
  );
});

export function LiveGuestCountAnnouncer({ total }) {
  const prevTotalRef = useRef(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const prev = prevTotalRef.current;
    if (prev !== null && prev !== total) {
      const diff = total - prev;
      if (diff > 0) {
        setMessage(
          diff === 1
            ? `1 new dancer joined. ${total} going now.`
            : `${diff} new dancers joined. ${total} going now.`,
        );
      } else {
        setMessage(`Guest list updated. ${total} going now.`);
      }
    }
    prevTotalRef.current = total;
  }, [total]);

  return (
    <div aria-live="polite" aria-atomic="true" className="sr-only" data-testid="guest-list-live-region">
      {message}
    </div>
  );
}

const GuestListHeader = memo(function GuestListHeader({ rsvps, total, counts, cap }) {
  const preview = rsvps.slice(0, 5);

  return (
      <div className="mb-3" data-testid="guest-list-header">
      <h2 className="font-ui font-semibold text-[22px] sm:text-[24px] leading-tight tracking-tight">
        Guest List (<span className="tabular-nums">{total}</span>)
      </h2>
      {total > 0 && (
        <div className="mt-3 space-y-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <div className="flex -space-x-2 shrink-0" aria-hidden={preview.length === 0}>
              {preview.map((r) => (
                <ProfileThumbnail
                  key={r.id}
                  name={r.name}
                  role={r.role}
                  thumbnailUrl={r.thumbnail_url}
                  hidden={r.hide_name}
                  size="sm"
                  className="ring-2 ring-canvas"
                />
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <Chip color="leaders">{Math.round(counts.balanced_leaders)} Leads</Chip>
              <Chip color="followers">{Math.round(counts.balanced_followers)} Follows</Chip>
              {counts.both > 0 && <Chip color="white">{counts.both} Both</Chip>}
              {cap && (
                <span className="font-ui text-[12px] font-medium text-ink/70">
                  {total} / {cap}
                </span>
              )}
            </div>
          </div>
          <BalanceChart counts={counts} />
        </div>
      )}
    </div>
  );
});

function FirstTimeMark() {
  return (
    <span className="inline-flex w-3.5 h-3.5 shrink-0 items-center justify-center rounded-full border-[1.5px] border-current text-[11px] font-bold leading-none">
      1
    </span>
  );
}

function MiniProfileField({ label, icon, children }) {
  return (
    <div className="py-3 first:pt-0 last:pb-0 border-b border-black/[0.06] last:border-0">
      <p className="font-ui text-[11px] font-medium text-ink/70 mb-1 flex items-center gap-1">
        {icon}
        {label}
      </p>
      <p className="font-ui text-[14px] font-medium leading-relaxed break-words">{children}</p>
    </div>
  );
}

function MiniProfileDetails({ miniProfile, ...rest }) {
  const hasAbout = !!miniProfile.about_me;
  const hasMusic = !!(miniProfile.favourite_songs || miniProfile.favourite_dance);
  const hasStatus = !!miniProfile.relationship_status;

  return (
    <div className="mt-3 pt-3 border-t border-black/[0.08]" {...rest}>
      {hasAbout && (
        <MiniProfileField label="About me">
          <LinkifiedText>{miniProfile.about_me}</LinkifiedText>
        </MiniProfileField>
      )}
      {hasMusic && (
        <MiniProfileField
          label="Favourite songs"
          icon={<Music2 className="w-3 h-3" strokeWidth={2.5} aria-hidden />}
        >
          <LinkifiedText>{miniProfile.favourite_songs || miniProfile.favourite_dance}</LinkifiedText>
        </MiniProfileField>
      )}
      {hasStatus && (
        <MiniProfileField
          label="Status"
          icon={<Heart className="w-3 h-3" strokeWidth={2.5} aria-hidden />}
        >
          {miniProfile.relationship_status}
        </MiniProfileField>
      )}
    </div>
  );
}

const GuestRowList = memo(function GuestRowList({ rsvps, tentative = false }) {
  let firstTimerLegendShown = false;
  let checkedInLegendShown = false;

  return rsvps.map((r, idx) => {
    const showFirstTimerText = !!(r.first_timer && !firstTimerLegendShown);
    const showCheckedInText = !!(r.checked_in_at && !checkedInLegendShown);
    if (r.first_timer) firstTimerLegendShown = true;
    if (r.checked_in_at) checkedInLegendShown = true;

    return (
      <GuestRow
        key={r.id}
        r={r}
        idx={idx}
        tentative={tentative}
        showFirstTimerText={showFirstTimerText}
        showCheckedInText={showCheckedInText}
      />
    );
  });
});

const GuestRow = memo(function GuestRow({
  r,
  idx,
  tentative = false,
  showFirstTimerText = true,
  showCheckedInText = true,
}) {
  const [open, setOpen] = useState(false);
  const hasMini = r.mini_profile && (
    r.mini_profile.about_me
    || r.mini_profile.favourite_songs
    || r.mini_profile.favourite_dance
    || r.mini_profile.relationship_status
  );
  const colors = ["", "bg-cream"];
  const status = attendanceStatus(r);

  const summary = (
    <>
      <ProfileThumbnail
        name={r.name}
        role={r.role}
        thumbnailUrl={r.thumbnail_url}
        hidden={r.hide_name}
        size="md"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-ui font-semibold text-[16px] truncate">{r.name}</p>
          {r.first_timer && (
            <Chip
              color="pink"
              ghost
              title="First time"
              aria-label="First time"
            >
              <FirstTimeMark />
              {showFirstTimerText && "First time"}
            </Chip>
          )}
          {r.checked_in_at && (
            <Chip
              color="amber"
              ghost
              title="Checked in"
              aria-label="Checked in"
              data-testid="guest-checked-in-tag"
            >
              <MapPin className="w-3 h-3" strokeWidth={3} aria-hidden />
              {showCheckedInText && "Checked in"}
            </Chip>
          )}
          {r.hide_name && <Chip color="ink" ghost>Hidden</Chip>}
          {tentative && (
            <Chip color={status === "not_coming" ? "ink" : "amber"} ghost>
              {attendanceLabel(status)}
            </Chip>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {r.role && <Chip color={ROLE_COLORS[r.role]}>{r.role}</Chip>}
          {(r.dance_forms || []).map((d) => (
            <Chip key={`${d.name}-${d.level}`} color="white">
              {d.name} · {d.level}
            </Chip>
          ))}
        </div>
      </div>
      {hasMini && (
        <ChevronDown
          className={`w-5 h-5 shrink-0 transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
          strokeWidth={3}
          aria-hidden
        />
      )}
    </>
  );

  return (
    <ReadableCard
      className={`p-3 sm:p-4 [content-visibility:auto] ${colors[idx % colors.length]}`}
      data-testid={`guest-row-${r.id}`}
    >
      {hasMini ? (
        <button
          type="button"
          onClick={() => setOpen((x) => !x)}
          className="w-full text-left flex items-center gap-3 min-h-[44px] cursor-pointer"
          aria-expanded={open}
          aria-label={`${open ? "Hide" : "Show"} profile for ${r.name}`}
        >
          {summary}
        </button>
      ) : (
        <div className="flex items-center gap-3">
          {summary}
        </div>
      )}

      {open && hasMini && (
        <MiniProfileDetails miniProfile={r.mini_profile} data-testid={`mini-profile-${r.id}`} />
      )}
    </ReadableCard>
  );
}, (prev, next) => (
  prev.r === next.r
  && prev.idx === next.idx
  && prev.tentative === next.tentative
  && prev.showFirstTimerText === next.showFirstTimerText
  && prev.showCheckedInText === next.showCheckedInText
));

/** Guest list section for event pages — live announcer, header, rows, tentative list. */
const EventGuestList = memo(function EventGuestList({
  comingRsvps,
  tentativeRsvps,
  counts,
  cap,
  showFollow = false,
  followerCount = 0,
  isFollowing = false,
  followBusy = false,
  onToggleFollow,
  isNotConfirmed = false,
}) {
  const followCopy = isNotConfirmed ? FOLLOW_COPY.proposed : FOLLOW_COPY.series;

  return (
    <>
      <LiveGuestCountAnnouncer total={counts.total} />
      <GuestListHeader
        rsvps={comingRsvps}
        total={counts.total}
        counts={counts}
        cap={cap}
      />

      {showFollow && (
        <EventFollowRow
          followerCount={followerCount}
          isFollowing={isFollowing}
          followBusy={followBusy}
          onToggleFollow={onToggleFollow}
          followCopy={followCopy}
        />
      )}

      <div className="space-y-3" data-testid="guest-list">
        {comingRsvps.length === 0 ? (
          <ReadableCard className="p-6 text-center bg-surface">
            <p className="font-ui font-semibold text-[16px] text-ink">No one&apos;s locked in yet. Be the icebreaker.</p>
            {showFollow && (
              <p className="mt-2 font-ui text-[14px] font-medium text-ink-secondary leading-relaxed" data-testid="event-follow-empty-hint">
                {followCopy.emptyHint}
              </p>
            )}
          </ReadableCard>
        ) : (
          <GuestRowList rsvps={comingRsvps} />
        )}
      </div>

      {tentativeRsvps.length > 0 && (
        <div className="mt-6">
          <h3 className="font-ui font-semibold text-[15px] text-ink/70 mb-3">
            Tentative · {tentativeRsvps.length}
          </h3>
          <div className="space-y-3" data-testid="tentative-guest-list">
            <GuestRowList rsvps={tentativeRsvps} tentative />
          </div>
        </div>
      )}
    </>
  );
});

export default EventGuestList;
